import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Badge variants using CVA
 *
 * Variants:
 * - default: Neutral gray badge
 * - primary: Blue primary badge
 * - success: Green success badge
 * - warning: Yellow/amber warning badge
 * - error: Red error/danger badge
 * - info: Blue info badge
 * - outline: Border-only badge
 */
export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors',
  {
    variants: {
      variant: {
        default:
          'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
        primary:
          'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
        secondary:
          'bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400',
        success:
          'bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-400',
        warning:
          'bg-warning-100 text-warning-700 dark:bg-warning-500/20 dark:text-warning-400',
        error:
          'bg-error-100 text-error-700 dark:bg-error-500/20 dark:text-error-400',
        info:
          'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
        outline:
          'border border-gray-300 bg-transparent text-gray-700 dark:border-gray-600 dark:text-gray-300',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

/**
 * Badge Component
 *
 * @param {Object} props
 * @param {'default'|'primary'|'secondary'|'success'|'warning'|'error'|'info'|'outline'} [props.variant='default']
 * @param {'sm'|'md'|'lg'} [props.size='md']
 * @param {string} [props.className]
 * @param {React.ReactNode} props.children
 */
const Badge = React.forwardRef(({ className, variant, size, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
});

Badge.displayName = 'Badge';

export { Badge };
export default Badge;
